import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Star } from 'lucide-react';
import { testimonialsApi } from '@/services/api';
import { toast } from 'sonner';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    client_name_en: '',
    client_name_ar: '',
    location_en: '',
    location_ar: '',
    content_en: '',
    content_ar: '',
    avatar: '',
    rating: 5
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await testimonialsApi.getAll();
      setTestimonials(response.data);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTestimonial) {
        await testimonialsApi.update(editingTestimonial.id, formData);
        toast.success('Testimonial updated');
      } else {
        await testimonialsApi.create(formData);
        toast.success('Testimonial added');
      }
      await fetchTestimonials();
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await testimonialsApi.remove(id);
      toast.success('Testimonial deleted');
      setTestimonials((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const openEditModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData({
      client_name_en: testimonial.client_name_en,
      client_name_ar: testimonial.client_name_ar,
      location_en: testimonial.location_en,
      location_ar: testimonial.location_ar,
      content_en: testimonial.content_en,
      content_ar: testimonial.content_ar,
      avatar: testimonial.avatar || '',
      rating: testimonial.rating
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
    setFormData({
      client_name_en: '',
      client_name_ar: '',
      location_en: '',
      location_ar: '',
      content_en: '',
      content_ar: '',
      avatar: '',
      rating: 5
    });
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Testimonials</h1>
          <p className="text-white/60">Manage client testimonials</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-testimonial-btn"
          className="flex items-center gap-2 bg-gold text-charcoal px-4 py-2 font-medium hover:bg-gold/90 transition-colors"
        >
          <Plus size={20} />
          Add Testimonial
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-32 bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white/5 border border-white/10 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold flex-shrink-0">
                    {testimonial.client_name_en.charAt(0)}
                  </div>
                  <div>
                    <div className="flex gap-1 mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-gold fill-gold" />
                      ))}
                    </div>
                    <p className="text-white/80 mb-3">"{testimonial.content_en}"</p>
                    <p className="text-white font-medium">{testimonial.client_name_en}</p>
                    <p className="text-gold text-sm">{testimonial.location_en}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(testimonial)}
                    data-testid={`edit-testimonial-${testimonial.id}`}
                    className="p-2 text-white/60 hover:text-gold transition-colors"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial.id)}
                    data-testid={`delete-testimonial-${testimonial.id}`}
                    className="p-2 text-white/60 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-brand-panel w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h2>
              <button onClick={closeModal} className="text-white/60 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Client Name (English)</label>
                  <input
                    type="text"
                    value={formData.client_name_en}
                    onChange={(e) => setFormData({ ...formData, client_name_en: e.target.value })}
                    required
                    data-testid="testimonial-name-en"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">Client Name (Arabic)</label>
                  <input
                    type="text"
                    value={formData.client_name_ar}
                    onChange={(e) => setFormData({ ...formData, client_name_ar: e.target.value })}
                    required
                    data-testid="testimonial-name-ar"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    dir="rtl"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white/60 text-sm mb-2">Location (English)</label>
                  <input
                    type="text"
                    value={formData.location_en}
                    onChange={(e) => setFormData({ ...formData, location_en: e.target.value })}
                    required
                    data-testid="testimonial-location-en"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    placeholder="e.g., Villa in Riyadh"
                  />
                </div>
                <div>
                  <label className="block text-white/60 text-sm mb-2">Location (Arabic)</label>
                  <input
                    type="text"
                    value={formData.location_ar}
                    onChange={(e) => setFormData({ ...formData, location_ar: e.target.value })}
                    required
                    data-testid="testimonial-location-ar"
                    className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none"
                    dir="rtl"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-2">Testimonial (English)</label>
                <textarea
                  value={formData.content_en}
                  onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                  required
                  rows={4}
                  data-testid="testimonial-content-en"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-2">Testimonial (Arabic)</label>
                <textarea
                  value={formData.content_ar}
                  onChange={(e) => setFormData({ ...formData, content_ar: e.target.value })}
                  required
                  rows={4}
                  data-testid="testimonial-content-ar"
                  className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 focus:border-gold outline-none resize-none"
                  dir="rtl"
                />
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="p-1"
                    >
                      <Star
                        className={`w-6 h-6 ${
                          star <= formData.rating ? 'text-gold fill-gold' : 'text-white/20'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 border border-white/20 text-white py-3 hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-testimonial-btn"
                  className="flex-1 bg-gold text-charcoal py-3 font-medium hover:bg-gold/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Save size={18} />
                  {saving ? 'Saving…' : 'Save Testimonial'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
